/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

import React, { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import APIHelper from '../api/APIHelper';
import envConfig from '../config/envConfig';
import { useTheme } from '../theme/ThemeContext';
import ThemedButton from '../components/common/ThemedButton';
import ThemedInput from '../components/common/ThemedInput';
import { inlineButton } from '../components/athlohive/EventFormFields';
import { readLookup } from '../components/athlohive/lookupOptions';

const apiHelper = new APIHelper({
  apiUrl: envConfig.backendApiUrl,
  tokenUrl: envConfig.backendTokenUrl,
  clientId: envConfig.clientKey,
  clientSecret: envConfig.clientSecret,
});

/**
 * The organizers events are run by.
 *
 * Until this screen existed the rows could only be seeded by a script, so an
 * admin could pick an organization in the event wizard but never add one.
 */

const STATUS_STYLES = {
  PENDING: { label: 'Pending', bg: '#fef3c7', fg: '#92400e' },
  APPROVED: { label: 'Approved', bg: '#dcfce7', fg: '#166534' },
  REJECTED: { label: 'Rejected', bg: '#fee2e2', fg: '#991b1b' },
  BLOCKED: { label: 'Blocked', bg: '#e5e7eb', fg: '#374151' },
};

function StatusPill({ status }) {
  const style = STATUS_STYLES[status] || { label: status || '—', bg: '#f3f4f6', fg: '#4b5563' };
  return (
    <span style={{
      background: style.bg, color: style.fg, borderRadius: 999,
      padding: '3px 10px', fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap',
    }}>
      {style.label}
    </span>
  );
}

export default function OrganizationListPage() {
  const { id: applicationId } = useParams();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const [search, setSearch] = useState('');

  const { data: application } = useQuery({
    queryKey: ['application', applicationId],
    queryFn: async ({ signal }) => {
      const resp = await apiHelper.apiCall(
        `/arcee/application/details/${applicationId}/`, { method: 'GET', signal }
      );
      if (!resp.ok) throw new Error('Could not load this application.');
      return resp.json();
    },
    enabled: !!applicationId,
    staleTime: 60_000,
  });

  const organizationId = application?.organization?.id
    ?? application?.organization_id
    ?? (typeof application?.organization === 'number' ? application.organization : undefined);

  const organizations = useQuery({
    queryKey: ['athlohiveOrganizations', applicationId, organizationId],
    queryFn: async ({ signal }) => {
      const resp = await apiHelper.apiCall('/arcee/application/table/fetch/', {
        method: 'POST',
        signal,
        body: JSON.stringify({
          table_name: 'organization',
          organization_id: organizationId,
          application_id: Number(applicationId),
          page_size: 200,
        }),
      });
      if (!resp.ok) throw new Error('Could not load organizations.');
      return readLookup(await resp.json()).rows;
    },
    enabled: !!organizationId && !!applicationId,
    staleTime: 10_000,
  });

  const rows = useMemo(() => {
    const all = organizations.data || [];
    const term = search.trim().toLowerCase();
    const matching = term
      ? all.filter((row) => `${row.name} ${row.city} ${row.verification_status}`
        .toLowerCase().includes(term))
      : all;
    return [...matching].sort((left, right) => (right.id ?? 0) - (left.id ?? 0));
  }, [organizations.data, search]);

  const cell = { padding: '12px 14px', borderBottom: '1px solid #eef0f3', fontSize: 14 };
  const base = `/dashboard/applications/${applicationId}/athlohive/organizations`;

  return (
    <div style={{ padding: 24, maxWidth: 1100, margin: '0 auto' }}>
      <div style={{ marginBottom: 6, fontSize: 13, color: '#6b7280' }}>
        <span
          style={{ cursor: 'pointer' }}
          onClick={() => navigate(`/dashboard/applications/${applicationId}`)}
        >
          {application?.name || 'Application'}
        </span>
        {' / Organizations'}
      </div>

      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        gap: 12, flexWrap: 'wrap', marginBottom: 18,
      }}>
        <h2 style={{ color: theme.primary, margin: 0 }}>Organizations</h2>
        <ThemedButton type="button" style={inlineButton} onClick={() => navigate(`${base}/create`)}>
          Add organization
        </ThemedButton>
      </div>

      <div style={{ maxWidth: 340, marginBottom: 16 }}>
        <ThemedInput
          placeholder="Search by name, city or status"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {organizations.isLoading && <div>Loading organizations…</div>}
      {organizations.isError && (
        <div style={{ color: '#d53b3b' }}>
          Organizations could not be loaded. Check that this account may read the organization table.
        </div>
      )}

      {!organizations.isLoading && !organizations.isError && (
        rows.length === 0 ? (
          <div style={{
            border: '1px dashed #d1d5db', borderRadius: 12, padding: 32,
            textAlign: 'center', color: '#6b7280',
          }}>
            {search
              ? `No organization matches “${search}”.`
              : 'No organizations yet. Add the first one, then it can host events.'}
          </div>
        ) : (
          <div style={{
            border: '1px solid #e5e7eb', borderRadius: 12, overflowX: 'auto', background: '#fff',
          }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 720 }}>
              <thead>
                <tr style={{ background: '#f9fafb', textAlign: 'left' }}>
                  <th style={{ ...cell, fontWeight: 700 }}>Name</th>
                  <th style={{ ...cell, fontWeight: 700 }}>Verification</th>
                  <th style={{ ...cell, fontWeight: 700 }}>City</th>
                  <th style={{ ...cell, fontWeight: 700 }}>Contact</th>
                  <th style={{ ...cell, fontWeight: 700, textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id}>
                    <td style={cell}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        {row.logo_url ? (
                          <img
                            src={row.logo_url}
                            alt=""
                            style={{
                              width: 32, height: 32, borderRadius: 6, objectFit: 'contain',
                              border: '1px solid #e5e7eb', background: '#fff',
                            }}
                            onError={(e) => { e.currentTarget.style.display = 'none'; }}
                          />
                        ) : null}
                        <span style={{ fontWeight: 600 }}>{row.name || '—'}</span>
                      </div>
                    </td>
                    <td style={cell}><StatusPill status={row.verification_status} /></td>
                    <td style={cell}>{row.city || '—'}</td>
                    <td style={cell}>{row.contact_email || '—'}</td>
                    <td style={{ ...cell, textAlign: 'right' }}>
                      <ThemedButton
                        type="button" variant="outlined" style={inlineButton}
                        onClick={() => navigate(`${base}/${row.id}/edit`)}
                      >
                        Edit
                      </ThemedButton>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}
    </div>
  );
}
